import { FC, useMemo, useState } from 'react';
import { useFlexLayout, useTable } from 'react-table';
import ReactTooltip from 'react-tooltip';

import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import axiosInstance from 'apis/axiosInstance';
import CommonButton from 'components/CommonButton/CommonButton';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { renderToast } from 'components/Toast/Toast';
import useClinicApiKeys from 'hooks/useClinicApiKeys';
import { IClinicApiKey } from 'interfaces/clinicApiKeys';
import { GENERIC_ERROR_MESSAGE, MAX_CLINIC_API_KEYS } from 'utils/constants';
import { datetimeFormat } from 'utils/datetime';
import CreateApiKeyModal from '../CreateApiKeyModal/CreateApiKeyModal';

const formatDate = (dateString: string) =>
  datetimeFormat({ dateString, pattern: 'MMM DD, YYYY' });

const ApiKeysTable: FC = () => {
  const { data, isLoading, mutate } = useClinicApiKeys();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [revokingKey, setRevokingKey] = useState<IClinicApiKey | null>(null);

  const apiKeys = useMemo(() => data ?? [], [data]);
  const hasReachedLimit = apiKeys.length >= MAX_CLINIC_API_KEYS;

  const columns = useMemo(
    () => [
      {
        Header: () => <div className="pl-1.7">Name</div>,
        accessor: (field: IClinicApiKey) => (
          <div className="pl-1.7">{field.name}</div>
        ),
        id: 'name',
        width: '200',
      },
      {
        Header: 'Key',
        accessor: (field: IClinicApiKey) => `${field.keyPrefix}…`,
        id: 'keyPrefix',
        width: '150',
      },
      {
        Header: 'Practices',
        accessor: (field: IClinicApiKey) => {
          const clinics = field.clinics ?? [];

          if (clinics.length <= 1) {
            return clinics[0]?.name ?? '';
          }

          const tooltipId = `api-key-clinics-${field.id}`;

          return (
            <div className="flex items-center gap-x-0.8">
              <span>{`${clinics.length} practices`}</span>
              <div data-for={tooltipId} data-tip="" className="cursor-pointer">
                <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
              </div>
              <ReactTooltip
                id={tooltipId}
                effect="solid"
                place="bottom"
                clickable
                delayHide={100}
                className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
              >
                <ul className="max-h-[24rem] overflow-y-auto overscroll-contain scrollbar text-11 text-darkest-grey font-normal text-left">
                  {clinics.map((clinic) => (
                    <li key={clinic.id}>{clinic.name}</li>
                  ))}
                </ul>
              </ReactTooltip>
            </div>
          );
        },
        id: 'clinics',
        width: '150',
      },
      {
        Header: 'Created Date',
        accessor: (field: IClinicApiKey) => formatDate(field.createdAt),
        id: 'createdAt',
        width: '150',
      },
      {
        Header: 'Last Used',
        accessor: (field: IClinicApiKey) =>
          field.lastUsedAt ? formatDate(field.lastUsedAt) : 'Never',
        id: 'lastUsedAt',
        width: '150',
      },
      {
        id: 'action_btn',
        width: '80',
        Cell: ({ row }: any) => (
          <CommonButton
            variant="text"
            className="text-magenta hover:text-magenta-lighter active:text-magenta-darker"
            onClick={() => setRevokingKey(row.original)}
          >
            Revoke
          </CommonButton>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: apiKeys }, useFlexLayout);

  const handleRevoke = async () => {
    if (!revokingKey) return;
    try {
      await axiosInstance.delete(`/clinic-api-keys/${revokingKey.id}`);
      renderToast({
        message: `API key "${revokingKey.name}" revoked successfully`,
        type: 'success',
      });
      await mutate();
      setRevokingKey(null);
    } catch (error) {
      renderToast({ message: GENERIC_ERROR_MESSAGE, type: 'error' });
    }
  };

  const displayTableBody = () => {
    if (isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[24rem] flex justify-center items-center">
                <LoadingSpinner className="all-child:fill-magenta" />
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    if (apiKeys.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                <EmptyBucket />
                <span>No API keys yet</span>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody {...getTableBodyProps()} className="text-darkest-grey">
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr
              {...row.getRowProps()}
              className="bg-white border-b border-light-grey px-2 group hover:bg-lightPink items-center"
            >
              {row.cells.map((cell: any) => (
                <td className="text-left p-0 py-1.9" {...cell.getCellProps()}>
                  {cell.render('Cell')}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <div className="mt-3">
      <div className="flex items-center gap-x-1.6">
        <h4 className="text-16 leading-[2.4rem] font-bold">API Keys</h4>
        <CommonButton
          variant="secondary"
          className="!min-h-[3.2rem]"
          disabled={isLoading || hasReachedLimit}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create API Key
        </CommonButton>
        {hasReachedLimit && (
          <span className="text-dim-grey">
            {`A practice can have up to ${MAX_CLINIC_API_KEYS} keys. Revoke a key to create a new one.`}
          </span>
        )}
      </div>
      <div className="bg-white shadow-primary rounded-[1rem] mt-2.4">
        <table {...getTableProps()} className="w-full text-14 leading-[2.1rem]">
          <thead className="font-bold">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="px-2">
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="text-left p-0 py-1.9"
                  >
                    <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                      {column.render('Header')}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {displayTableBody()}
        </table>
      </div>
      {isCreateModalOpen && (
        <CreateApiKeyModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={mutate}
        />
      )}
      {revokingKey && (
        <ConfirmationModal
          title="Revoke API key?"
          description={`Apps using "${revokingKey.name}" will stop working immediately${
            revokingKey.clinics?.length > 1
              ? ` for all ${revokingKey.clinics.length} practices`
              : ''
          }. This cannot be undone.`}
          submitBtnTitle="Revoke"
          onClose={() => setRevokingKey(null)}
          onSubmit={handleRevoke}
        />
      )}
    </div>
  );
};

export default ApiKeysTable;
